import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
const buttonVariants = cva("inline-flex items-center justify-center gap-2 whitespace-normal text-center leading-tight font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0", {
  variants: {
    variant: {
      default: "bg-[hsl(var(--h2h-black))] text-background rounded-full hover:opacity-90 active:opacity-80 shadow-md",
      destructive: "bg-destructive text-destructive-foreground rounded-md hover:bg-destructive/90",
      outline: "border border-foreground/20 bg-background rounded-full hover:bg-muted shadow-sm",
      secondary: "bg-secondary text-secondary-foreground rounded-full hover:bg-secondary/80",
      ghost: "hover:bg-accent hover:text-accent-foreground rounded-md",
      link: "text-foreground underline-offset-4 hover:underline",
      hero: "bg-[hsl(var(--h2h-black))] text-background rounded-full hover:opacity-90 active:opacity-80 shadow-lg",
      heroOutline: "border border-foreground/20 bg-background/80 backdrop-blur-sm text-foreground rounded-full hover:bg-muted/50",
      warm: "bg-[hsl(var(--h2h-coral))] text-white rounded-full hover:opacity-90 shadow-md",
      calm: "bg-[hsl(var(--h2h-cyan))] text-foreground rounded-full hover:opacity-90 shadow-md"
    },
    size: {
      default: "min-h-12 px-5 py-2 text-sm",
      sm: "min-h-11 px-4 py-1.5 text-xs",
      lg: "min-h-12 px-7 py-2 text-base",
      xl: "min-h-14 px-9 py-2 text-lg",
      icon: "h-11 w-11"
    }
  },
  defaultVariants: {
    variant: "default",
    size: "default"
  }
});
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  size,
  asChild = false,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : "button";
  return <Comp className={cn(buttonVariants({
    variant,
    size,
    className
  }))} ref={ref} {...props} />;
});
Button.displayName = "Button";
export { Button, buttonVariants };